import React, { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";

import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
// The shared error-message ladder (Architecture.md rule 16). It already covers everything a local
// helper would: axios errors with a response body, PascalCase or camelCase Message, string bodies, and
// the timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../../../common/concurrency";
import { useCommittedFilterRows } from "../../../../../common/reportFilters";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../../../reports/components/CommonReport";
import OperationResultGrid from "../../../charges/common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { PREVIEW_COLUMNS, PREVIEW_KEY_EXPR } from "./PreviewGridColumns";
import "./transferShipmentsToDispatch.css";
import {
    BUTTON,
    COMMENT_MAX_LENGTH,
    DEFAULT_LAYOUT_NAME,
    HELP_TEXT,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    PREVIEW_GRID_KEY,
    PREVIEW_TITLE,
    REPORT_FULL_NAME,
    REPORT_NAME,
    REQUEST_TIMEOUT_MS,
    RESULT_CAPTION,
    RESULT_LABELS,
    RESULT_OK,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", PREVIEWING: "previewing", STARTING: "starting" });

/**
 * Puts a class on the Preview popup's WRAPPER so the stylesheet can reach `.dx-popup-content` — the
 * element DevExtreme sizes and the one that has to become a flex column for the grid to fill it.
 * There is no other hook: the popup renders into an overlay outside this component's DOM subtree.
 *
 * Module-level so the object identity is stable; an inline literal would be a new object every render
 * and make the Popup re-apply it.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * A function, not a module-level constant: Reset must hand back a fresh object, and a shared one would
 * be mutated across resets.
 */
const initialFormFields = () => ({
    Comment: "",
});

/**
 * The rows of the result dialog: Batch Number and Records Processed, exactly the two values
 * TfrmABD0013 takes (uWMD0077.pas:139-145).
 *
 * No zero-record branch: unlike ABD0013, which opens with two blank boxes and a "nothing matched"
 * caption (uABD0013.PAS:63-79), this dialog is only ever shown for a run that transferred something. A
 * run that matched nothing reports through an error toast instead — the same deliberate deviation the
 * sibling operation screens make.
 *
 * Tolerates a null result because the dialog's content is rendered unconditionally — see the Popup.
 */
const resultRows = (result) => {
    const value = result ?? {};

    return [
        { label: RESULT_LABELS.BatchNumber, value: value.BatchNumber ?? "" },
        { label: RESULT_LABELS.RecordsProcessed, value: value.RecordsProcessed ?? "" },
    ];
};

/**
 * WM "Transfer Shipments To Dispatch" (legacy WMD0077).
 *
 * Start runs SPWM_BATCHTRANSFERTODISPATCH server-side and reports the batch number and the number of
 * records transferred. Preview runs the same filter through a read-only SELECT.
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write, so
 * Architecture.md rules 4-6, 12 and 14-16 (including optimistic concurrency) do not apply.
 *
 * Simpler than its siblings in one way worth noting: there is no confirmation round-trip. The Delphi
 * form asked nothing before starting, and nothing about this operation's inputs is destructive enough
 * to warrant inventing one — an empty filter transfers every eligible header, but transferring is
 * idempotent-ish (a second run updates the existing dispatch object rather than duplicating it).
 *
 * The legacy Help and Exit buttons are dropped: Help opened a Delphi context topic and Exit closed the
 * MDI child, neither of which has a web equivalent.
 */
const TransferShipmentsToDispatch = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const previewGrid = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so
     * three separate updates after the fetch meant three extra renders of the page — and each one
     * rebuilt the grid's toolbar template, remounting GridLayoutManager and firing another
     * GetGridLayouts request.
     */
    const [preview, setPreview] = useState({ rows: [], visible: false, hasRun: false });
    const [previewLayout, setPreviewLayout] = useState(DEFAULT_LAYOUT_NAME);

    const [result, setResult] = useState(null);

    const isBusy = busy !== BUSY.IDLE;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    /**
     * CommonReport fires this once, from loadReport's .then. It stays correct because the editable
     * filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits mutate
     * in place — so the reference captured here stays live. Do not clone it per render.
     */
    const handleReportStateChange = useCallback((reportState) => {
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, []);

    const buildRequest = useCallback(
        (rows) => ({
            Comment: (formFields.Comment || "").slice(0, COMMENT_MAX_LENGTH),
            FilterWhereInfoList: rows || [],
        }),
        [formFields]
    );

    // Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
    // the onReportStateChange array is the wrong source at submit time.
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    // Stable identity: an inline arrow here would change on every render and, via the Popup, keep the
    // grid subtree re-rendering.
    const hidePreview = useCallback(() => {
        setPreview((previous) => ({ ...previous, visible: false }));
    }, []);

    const closeResult = useCallback(() => setResult(null), []);

    /**
     * Required, even though this screen has nothing to do on load: FormField calls onFormLoad() in
     * fetchData's `finally` with no guard and no default (FormField.js:256), so omitting it throws
     * "onFormLoad is not a function" and the parameters panel never renders.
     *
     * The sibling operation screens do real work here — they imperatively feed their SelectBoxes via
     * loadLookupFromStaticDataSource, because the dynamic form cannot declare a static option list.
     * This screen's only parameter is a textarea, so there is nothing to load.
     *
     * useCallback so the identity is stable across renders rather than a fresh arrow each time.
     */
    const handleFormLoad = useCallback(() => { }, []);

    const handlePreview = useCallback(async () => {
        // Commits any in-progress filter cell first, so Preview matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMTransferShipmentsToDispatch.GetTransactions}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;

            // DEV-5355 F2. A row array is the ONLY shape treated as success; anything else is an error,
            // whatever the envelope claims.
            //
            // Two failure modes reach here as HTTP 200. One is a refusal decided BEFORE the query runs —
            // a filter naming a column this screen cannot reach, an unusable value, an over-long filter —
            // which arrives as Success:false with no Resultdata. The other fails DURING the query, and
            // that is the one a Success-only check misses. Requiring the array closes both, and any
            // future failure mode, without depending on the envelope being filled in correctly.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setPreview({ rows: [], visible: false, hasRun: false });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setPreview({ rows: payload.Resultdata, visible: true, hasRun: true });
        } catch (error) {
            setPreview({ rows: [], visible: false, hasRun: false });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [readFilterRows, buildRequest, postOptions]);

    const handleStart = useCallback(async () => {
        const filterRows = await readFilterRows();

        setBusy(BUSY.STARTING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMTransferShipmentsToDispatch.Start}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;
            const data = payload?.Resultdata ?? {};

            // The dialog reports a run that transferred something; everything else is an error toast.
            // That covers a server-side refusal (no Resultdata at all) and a run that matched nothing,
            // where the server sends Messages.NoRecordsFoundWithinRange.
            //
            // Note this is NOT what Delphi does: ABD0013 opens either way, showing blank boxes and the
            // "nothing matched" text in its caption band (uABD0013.PAS:63-79). A toast was preferred
            // here, consistently with the sibling operation screens.
            if (payload?.Resultdata == null || (data.RecordsProcessed ?? 0) <= 0) {
                notify(
                    { message: payload?.Message || MESSAGES.NO_RECORD_FOUND_RANGE, width: 450 },
                    "error",
                    5000
                );
                return;
            }

            setResult(data);
        } catch (error) {
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [readFilterRows, buildRequest, postOptions]);

    const handleReset = useCallback(() => {
        setFormFields(initialFormFields());
        setPreview({ rows: [], visible: false, hasRun: false });
        setResult(null);
        // Re-fetches the field definitions, rebuilds the filter store and re-fires onReportStateChange
        // with a fresh array (wbTransferToDispatch.Reset, uWMD0077.pas:158).
        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, []);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={
                    busy === BUSY.STARTING
                        ? MESSAGES.TRANSFERRING_SHIPMENTS_TO_DISPATCH
                        : MESSAGES.PREVIEWING_SHIPMENTS_TO_DISPATCH
                }
            />

            {/*
                Delphi's grpMain is a centred bold title band above the where-builder. In the web app the
                page title lives in the toolbar, so the band collapses into this header rather than being
                reproduced as a separate strip.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="tsd-page">
                {/*
                    The Delphi read-only memo (edtMemo), which sits at the TOP of the form — directly
                    under the title band and above the Record Range — not beside the parameters. It
                    carries no caption there, so none is rendered here either.
                */}
                <div className="op-help tsd-help">{HELP_TEXT}</div>

                <div className="op-filter">
                    <CommonReport
                        name={REPORT_NAME}
                        fullName={REPORT_FULL_NAME}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>

                {/* Delphi's grpBottomMiddle: an untitled group box holding only the Comment memo. */}
                <div className="op-parameters tsd-parameters">
                    {/*
                        hasOnlyOneColumn because there is exactly one group. Without it FormField gives
                        the group col-md-4, i.e. a third of the page, which is unreadably narrow for a
                        single memo.
                    */}
                    <FormField
                        currrentUser={currentUser}
                        layoutId={LAYOUT_ID}
                        layoutName={LAYOUT_NAME}
                        defaultFormSchema={ParametersLayout}
                        formFields={formFields}
                        setformFields={setFormFields}
                        DYNAMICFORMSETTING={dynamicFormSetting}
                        formRefs={formRefs}
                        onFormLoad={handleFormLoad}
                        hasOnlyOneColumn={true}
                    />
                </div>
            </div>

            <Popup
                visible={preview.visible}
                onHiding={hidePreview}
                hideOnOutsideClick={true}
                showCloseButton={true}
                title={PREVIEW_TITLE}
                width="94vw"
                height="76vh"
                wrapperAttr={PREVIEW_POPUP_ATTR}
            >
                {/*
                    The grid FILLS the popup rather than being given a computed height — see
                    .op-preview-popup in the shared operationScreen.css.

                    It used to be height="calc(<popup vh> - 144px)". That subtraction has to guess the
                    popup's chrome plus the footer band, and it guessed high — leaving a dead strip below
                    the row count that grew or shrank with the viewport. Flexing instead means the grid
                    absorbs whatever is left over, so there is nothing to keep in step and no gap at any
                    window size. The sibling charges screens have since been moved onto the same classes.

                    No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller would
                    give it an unbounded width to lay out against, killing horizontal scrolling.
                */}
                <div className="op-preview-body">
                    <div className="op-preview-grid">
                        <OperationResultGrid
                            rows={preview.rows}
                            columns={PREVIEW_COLUMNS}
                            keyExpr={PREVIEW_KEY_EXPR}
                            noDataText={preview.hasRun ? MESSAGES.TRANSFER_TO_DISPATCH_NO_TRANSACTIONS_FOUND : ""}
                            height="100%"
                            gridRef={previewGrid}
                            gridKey={PREVIEW_GRID_KEY}
                            currentLayout={previewLayout}
                            setCurrentLayout={setPreviewLayout}
                            accessToken={accessToken}
                        />
                    </div>

                    {/*
                        The row count, as TLookupForm shows it under its grid (SearchFm.pas lbInfo).

                        Counted from preview.rows, i.e. the whole result set — NOT the grid's current
                        page or its filter-row state. Note this is a count of HEADERS; the number Start
                        reports can be higher, because a header whose FM Transfer flag is "using
                        Sublots" yields one transferred record per sublot.

                        Hidden at zero rows: the grid's noDataText already covers that.
                    */}
                    {preview.rows.length > 0 && (
                        <div className="op-preview-footer">
                            {MESSAGES.PREVIEW_RECORDS_MATCHING_FORMAT.replace("{0}", preview.rows.length)}
                        </div>
                    )}
                </div>
            </Popup>

            {/*
                The legacy result dialog, TfrmABD0013 (uWMD0077.pas:145).

                OK is the only way out, matching the Delphi form: uABD0013.DFM sets BorderStyle bsSingle
                with BorderIcons reduced (no close box) and FormStyle fsStayOnTop, and it is shown with
                ShowModal — so there is no X and clicking away does nothing. A batch result carrying a
                batch number is worth an explicit acknowledgement.
            */}
            <Popup
                visible={result != null}
                onHiding={closeResult}
                hideOnOutsideClick={false}
                showCloseButton={false}
                title={MESSAGES.PROCESS_COMPLETE}
                width={460}
                height="auto"
            >
                {/*
                    Rendered unconditionally. Wrapping this in `{result && …}` means that when DevExtreme
                    creates the Popup's content template `result` is still null, so it captures an empty
                    template and never fills it — the modal opens showing its title and nothing else.
                    resultRows tolerates null so the hidden state renders harmless placeholders.
                */}
                <div className="op-result">
                    {/* Delphi shows the caller-supplied caption in a read-only memo above the counts. */}
                    <div className="op-result-caption">{RESULT_CAPTION}</div>
                    {resultRows(result).map((row) => (
                        <div className="op-result-row" key={row.label}>
                            <span className="op-result-label">{row.label}</span>
                            <span className="op-result-value">{row.value}</span>
                        </div>
                    ))}
                    <div className="op-result-actions">
                        <Button text={RESULT_OK} type="default" onClick={closeResult} />
                    </div>
                </div>
            </Popup>
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(TransferShipmentsToDispatch);
